// Modul pro správu sekce Soubory (admin)
// - Tabulka souborů s filtrem podle zákazníka
// - Akce: stáhnout, sdílet odkaz, smazat
// - Upload je implementován ve Fázi 3 (zatím placeholder)

import { icon, getFileIconSvg } from './icons.js';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:1234/api'
    : '/api';

const FILE_STATUS_META = {
  active:   { label: 'Aktivní',     badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60' },
  archived: { label: 'Archivovaný', badge: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200' }
};

// Zakázané přípony (FE pre-validace; backend validuje znovu)
const BLOCKED_EXTENSIONS = ['exe', 'sh', 'bat', 'ps1', 'cmd', 'com', 'app', 'msi', 'scr', 'vbs', 'js'];

function getFileExtension(name) {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}

export class FilesManager {
  constructor() {
    this.files = [];
    this.selectedCustomer = null;     // { id, name } | null
    this.includeArchived = false;
    this.loading = false;
    this.error = null;
    this._inited = false;
    this.stats = null;                // { totals, perCustomer } | null
  }

  /**
   * Voláno při aktivaci sekce. Idempotentní – načte data při prvním zobrazení,
   * při dalších přechodech volá pouze refresh().
   */
  async showSection() {
    if (!this._inited) {
      this._inited = true;
      this.renderShell();
    }
    // Pokud byl filtr nastaven externě (např. z customers.js před přechodem),
    // refresh label + checkbox po každém vstupu do sekce.
    this.updateCustomerLabel();
    const archivedToggle = document.getElementById('files-archived-toggle');
    if (archivedToggle) archivedToggle.checked = this.includeArchived;
    await this.loadFiles();
  }

  /**
   * Veřejné refresh API (např. po uploadu / smazání externě).
   */
  refresh() {
    return this.loadFiles();
  }

  // ===== API =====

  async loadFiles() {
    this.loading = true;
    this.error = null;
    this.renderBody();

    try {
      const params = new URLSearchParams();
      if (this.selectedCustomer?.id) params.set('customerId', this.selectedCustomer.id);
      if (this.includeArchived) params.set('includeArchived', 'true');
      const url = `${API_BASE_URL}/files${params.toString() ? `?${params}` : ''}`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = await res.json();
      this.files = Array.isArray(json.data) ? json.data : [];
    } catch (err) {
      console.error('Chyba při načítání souborů:', err);
      this.error = err.message || 'Nepodařilo se načíst soubory';
      this.files = [];
    } finally {
      this.loading = false;
      this.renderBody();
    }

    // Stats refresh (fire-and-forget, neblokuje hlavní seznam)
    this.loadStats();
  }

  async loadStats() {
    try {
      const res = await fetch(`${API_BASE_URL}/files/stats`, { credentials: 'include' });
      if (!res.ok) return;
      const json = await res.json();
      this.stats = json.data || null;
      this.renderStats();
    } catch (err) {
      console.warn('Statistiky se nepodařilo načíst:', err);
    }
  }

  async deleteFile(fileId, label) {
    if (!confirm(`Opravdu chcete smazat soubor „${label}"?\n\nTato akce je nevratná.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/files/${encodeURIComponent(fileId)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Smazání selhalo');
      this.notify('Soubor byl smazán', 'success');
      this.loadFiles();
    } catch (err) {
      console.error('Chyba při mazání souboru:', err);
      this.notify('Chybu při mazání souboru', 'error');
    }
  }

  async createShareLink(customerId, customerName) {
    try {
      const res = await fetch(`${API_BASE_URL}/files/share/${encodeURIComponent(customerId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerName })
      });
      if (!res.ok) throw new Error('Vytvoření odkazu selhalo');
      const json = await res.json();
      return json.data; // { token, url, ... }
    } catch (err) {
      console.error('Chyba při vytváření odkazu:', err);
      this.notify('Nepodařilo se vytvořit veřejný odkaz', 'error');
      return null;
    }
  }

  async shareCustomerFiles(customerId, customerName) {
    const linkData = await this.createShareLink(customerId, customerName);
    if (!linkData) return;

    const url = linkData.url;
    try {
      await this.copyToClipboard(url);
      this.notify(`Odkaz pro ${customerName} byl zkopírován do schránky`, 'success');
    } catch (_) {
      // Fallback – zobrazí prompt
      window.prompt('Veřejný odkaz pro zákazníka (zkopírujte ručně):', url);
    }
  }

  // ===== Render: shell (top bar + container, jen jednou) =====

  renderShell() {
    const root = document.getElementById('soubory-section-content');
    if (!root) return;

    root.innerHTML = `
      <div class="w-full">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900 mb-1">Soubory</h2>
            <p class="text-gray-600 text-sm mb-0">Sdílení souborů s klienty přes veřejný odkaz.</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button id="files-upload-btn"
              class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              ${icon('cloud-arrow-up', 'w-4 h-4 mr-2')}
              Nahrát soubor
            </button>
            <button id="files-maintenance-btn"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Hromadně smazat staré soubory">
              ${icon('archive-box', 'w-4 h-4 mr-2 text-gray-500')}
              Údržba
            </button>
          </div>
        </div>

        <div id="files-stats" class="mb-4"></div>

        <div class="bg-white shadow-sm rounded-lg border border-gray-200 p-4 mb-4">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div class="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <span class="text-sm text-gray-500 whitespace-nowrap">Zákazník:</span>
                <div class="flex items-center gap-2 min-w-0">
                  <button id="files-pick-customer-btn"
                    class="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 min-w-0">
                    ${icon('magnifying-glass', 'w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0')}
                    <span id="files-customer-label" class="truncate max-w-[18rem]">Všichni zákazníci</span>
                  </button>
                  <button id="files-clear-customer-btn"
                    class="hidden inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    title="Zrušit filtr">
                    ${icon('x-mark', 'w-4 h-4')}
                  </button>
                </div>
              </div>

              <label class="inline-flex items-center text-sm text-gray-700 select-none">
                <input id="files-archived-toggle" type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2">
                Zobrazit i archivované
              </label>
            </div>

            <div id="files-customer-actions" class="hidden flex items-center gap-2">
              <button id="files-share-btn"
                class="inline-flex items-center px-3 py-1.5 border border-emerald-300 rounded-md text-sm font-medium text-emerald-700 bg-white hover:bg-emerald-50">
                ${icon('link', 'w-4 h-4 mr-1.5')}
                Zkopírovat odkaz pro klienta
              </button>
            </div>
          </div>
        </div>

        <div id="files-summary" class="mb-3 text-xs text-gray-500"></div>
        <div id="files-body"></div>
      </div>
    `;

    this.bindShellEvents();
  }

  bindShellEvents() {
    document.getElementById('files-upload-btn')?.addEventListener('click', () => this.openUploadModal());
    document.getElementById('files-maintenance-btn')?.addEventListener('click', () => this.openMaintenanceModal());

    document.getElementById('files-pick-customer-btn')?.addEventListener('click', () => this.pickCustomer());
    document.getElementById('files-clear-customer-btn')?.addEventListener('click', () => this.clearCustomerFilter());

    document.getElementById('files-archived-toggle')?.addEventListener('change', (e) => {
      this.includeArchived = !!e.target.checked;
      this.loadFiles();
    });

    document.getElementById('files-share-btn')?.addEventListener('click', () => {
      if (!this.selectedCustomer) return;
      this.shareCustomerFiles(this.selectedCustomer.id, this.selectedCustomer.name);
    });
  }

  async pickCustomer() {
    if (!window.customersManager) {
      this.notify('Zákazníci nejsou ještě načteni', 'warning');
      return;
    }
    const customer = await window.customersManager.openSelector();
    if (!customer) return;
    this.selectedCustomer = { id: customer.id, name: customer.name };
    this.updateCustomerLabel();
    this.loadFiles();
  }

  clearCustomerFilter() {
    this.selectedCustomer = null;
    this.updateCustomerLabel();
    this.loadFiles();
  }

  updateCustomerLabel() {
    const label = document.getElementById('files-customer-label');
    const clearBtn = document.getElementById('files-clear-customer-btn');
    const actions = document.getElementById('files-customer-actions');
    if (this.selectedCustomer) {
      if (label) label.textContent = this.selectedCustomer.name;
      clearBtn?.classList.remove('hidden');
      actions?.classList.remove('hidden');
      actions?.classList.add('flex');
    } else {
      if (label) label.textContent = 'Všichni zákazníci';
      clearBtn?.classList.add('hidden');
      actions?.classList.add('hidden');
      actions?.classList.remove('flex');
    }
  }

  // ===== Render: body (table / empty / error / loading) =====

  renderBody() {
    const container = document.getElementById('files-body');
    const summary = document.getElementById('files-summary');
    if (!container) return;

    if (this.loading) {
      container.innerHTML = `
        <div class="flex justify-center p-8">
          ${icon('spinner', 'animate-spin h-8 w-8 text-blue-500', { role: 'status' })}
          <span class="sr-only">Načítám…</span>
        </div>
      `;
      if (summary) summary.textContent = '';
      return;
    }

    if (this.error) {
      container.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex items-start">
            ${icon('exclamation-circle', 'h-5 w-5 text-red-400 mt-0.5')}
            <div class="ml-3 flex-1">
              <h3 class="text-sm font-medium text-red-800">${this.escape(this.error)}</h3>
              <button id="files-retry-btn" class="mt-2 text-sm text-red-600 hover:text-red-500 underline">Zkusit znovu</button>
            </div>
          </div>
        </div>
      `;
      document.getElementById('files-retry-btn')?.addEventListener('click', () => this.loadFiles());
      if (summary) summary.textContent = '';
      return;
    }

    if (this.files.length === 0) {
      const inFilter = !!this.selectedCustomer;
      container.innerHTML = `
        <div class="bg-white rounded-lg border border-gray-200 p-10 text-center">
          ${icon('folder-open', 'mx-auto h-12 w-12 text-gray-300 mb-3')}
          <h3 class="text-base font-medium text-gray-900 mb-1">
            ${inFilter ? 'Tento zákazník zatím nemá žádné soubory' : 'Zatím nejsou nahrané žádné soubory'}
          </h3>
          <p class="text-sm text-gray-500">
            ${inFilter
              ? 'Nahrajte první soubor pro tohoto zákazníka.'
              : 'Začněte nahráním souboru a přiřazením k zákazníkovi.'}
          </p>
        </div>
      `;
      if (summary) summary.textContent = '';
      return;
    }

    // Souhrn nahoře
    if (summary) {
      const activeCount = this.files.filter((f) => f.status !== 'archived').length;
      const archivedCount = this.files.length - activeCount;
      const totalSize = this.files.reduce((sum, f) => sum + (Number(f.size) || 0), 0);
      const parts = [
        `${this.files.length} ${this.plural(this.files.length, 'soubor', 'soubory', 'souborů')}`,
        `${this.formatSize(totalSize)} celkem`
      ];
      if (archivedCount > 0) parts.push(`${archivedCount} archivovaných`);
      summary.textContent = parts.join(' · ');
    }

    // Tabulka
    container.innerHTML = `
      <div class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Soubor</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Zákazník</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Nahráno</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Velikost</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Stav</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              ${this.files.map((f) => this.renderRow(f)).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    this.bindRowEvents(container);
  }

  renderRow(file) {
    const iconSvg = this.getFileIconSvg(file.mimeType, file.originalName);
    const meta = FILE_STATUS_META[file.status] || FILE_STATUS_META.active;
    const uploadDate = this.formatDateTime(file.uploadedAt);
    const sizeStr = this.formatSize(file.size);
    const customerName = file.customerName || file.customerId || '-';
    const isArchived = file.status === 'archived';

    return `
      <tr class="hover:bg-gray-50 ${isArchived ? 'opacity-70' : ''}">
        <td class="px-4 py-3 align-middle">
          <div class="w-9 h-9 rounded-md bg-gray-50 ring-1 ring-gray-200 flex items-center justify-center text-gray-500">
            ${iconSvg}
          </div>
        </td>
        <td class="px-4 py-3 align-middle min-w-0">
          <div class="text-sm font-medium text-gray-900 truncate max-w-[28rem]" title="${this.escape(file.originalName)}">${this.escape(file.originalName)}</div>
          ${file.description ? `<div class="text-xs text-gray-500 truncate max-w-[28rem] mt-0.5" title="${this.escape(file.description)}">${this.escape(file.description)}</div>` : ''}
          <div class="text-xs text-gray-500 md:hidden mt-1">${this.escape(customerName)} · ${this.escape(uploadDate)} · ${sizeStr}</div>
        </td>
        <td class="px-4 py-3 align-middle hidden md:table-cell">
          <button class="text-sm text-blue-600 hover:text-blue-800 hover:underline" data-action="filter-customer" data-id="${this.escape(file.customerId)}" data-name="${this.escape(customerName)}">
            ${this.escape(customerName)}
          </button>
        </td>
        <td class="px-4 py-3 align-middle hidden lg:table-cell">
          <span class="text-sm text-gray-700">${this.escape(uploadDate)}</span>
          ${file.uploadedBy ? `<div class="text-xs text-gray-400">${this.escape(file.uploadedBy)}</div>` : ''}
        </td>
        <td class="px-4 py-3 align-middle text-right hidden sm:table-cell">
          <span class="text-sm text-gray-700 tabular-nums">${sizeStr}</span>
        </td>
        <td class="px-4 py-3 align-middle hidden md:table-cell">
          <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full ${meta.badge}">${this.escape(meta.label)}</span>
          ${file.downloadCount > 0 ? `<div class="inline-flex items-center gap-0.5 text-xs text-gray-400 mt-0.5">${icon('arrow-down-tray', 'w-3 h-3')} ${file.downloadCount}×</div>` : ''}
        </td>
        <td class="px-4 py-3 align-middle text-right whitespace-nowrap">
          <div class="inline-flex items-center gap-1">
            <button data-action="download" data-id="${this.escape(file.id)}" data-url="${this.escape(file.storageUrl || '')}" data-name="${this.escape(file.originalName)}"
              class="inline-flex items-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100" title="Stáhnout">
              ${icon('arrow-down-tray', 'w-4 h-4')}
            </button>
            <button data-action="share" data-customer-id="${this.escape(file.customerId)}" data-customer-name="${this.escape(customerName)}"
              class="inline-flex items-center p-1.5 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Sdílet zákazníkovi (vytvoří/zkopíruje odkaz)">
              ${icon('link', 'w-4 h-4')}
            </button>
            ${!isArchived ? `
            <button data-action="replace" data-id="${this.escape(file.id)}"
              class="inline-flex items-center p-1.5 rounded-md text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Nahradit novou verzí">
              ${icon('arrows-right-left', 'w-4 h-4')}
            </button>` : ''}
            <button data-action="delete" data-id="${this.escape(file.id)}" data-name="${this.escape(file.originalName)}"
              class="inline-flex items-center p-1.5 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50" title="Smazat">
              ${icon('trash', 'w-4 h-4')}
            </button>
          </div>
        </td>
      </tr>
    `;
  }

  bindRowEvents(container) {
    container.querySelectorAll('[data-action="filter-customer"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.dataset.id;
        const name = btn.dataset.name;
        if (!id) return;
        this.selectedCustomer = { id, name };
        this.updateCustomerLabel();
        this.loadFiles();
      });
    });

    container.querySelectorAll('[data-action="download"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.dataset.id;
        const url = btn.dataset.url;
        const name = btn.dataset.name;
        this.downloadFile(id, url, name);
      });
    });

    container.querySelectorAll('[data-action="share"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const customerId = btn.dataset.customerId;
        const customerName = btn.dataset.customerName;
        if (customerId) this.shareCustomerFiles(customerId, customerName);
      });
    });

    container.querySelectorAll('[data-action="replace"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openReplaceModal(btn.dataset.id);
      });
    });

    container.querySelectorAll('[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.deleteFile(btn.dataset.id, btn.dataset.name);
      });
    });
  }

  // ===== Akce: download =====

  downloadFile(fileId, storageUrl, filename) {
    if (!storageUrl) {
      this.notify('Soubor nemá uloženou adresu', 'error');
      return;
    }

    // Pro lokální dev URL doplníme absolutní origin backend serveru
    let absoluteUrl = storageUrl;
    if (storageUrl.startsWith('/api/')) {
      absoluteUrl = `${API_BASE_URL.replace(/\/api$/, '')}${storageUrl}`;
    }

    // Pro stažení vytvoříme dočasný link
    const a = document.createElement('a');
    a.href = absoluteUrl;
    a.download = filename || '';
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // ===== Upload modal =====

  /**
   * Otevře upload modal. Pokud je nastaven `replaceOf`, modal poběží v replace-režimu –
   * zákazník je předvyplněný + uzamčený, po úspěšném uploadu se starý soubor archivuje.
   *
   * @param {object} [opts]
   * @param {string} [opts.replaceOf] - ID souboru, který se má nahradit
   * @param {{id:string,name:string}} [opts.customer] - Předvyplněný zákazník (např. z aktivního filtru)
   */
  openUploadModal(opts = {}) {
    const replaceOf = opts.replaceOf || null;
    const oldFile = replaceOf ? this.files.find((f) => f.id === replaceOf) : null;

    // Předvyplnění zákazníka:
    // 1) replace mode – ze starého souboru (uzamčeno)
    // 2) jinak z aktivního filtru
    let customer = null;
    if (oldFile) {
      customer = { id: oldFile.customerId, name: oldFile.customerName || '' };
    } else if (opts.customer) {
      customer = opts.customer;
    } else if (this.selectedCustomer) {
      customer = { ...this.selectedCustomer };
    }

    this.renderUploadModal({ replaceOf, oldFile, customer });
  }

  openReplaceModal(fileId) {
    this.openUploadModal({ replaceOf: fileId });
  }

  renderUploadModal({ replaceOf, oldFile, customer }) {
    const modalId = 'files-upload-modal';
    this.removeModal(modalId);

    const isReplace = !!replaceOf;
    const title = isReplace
      ? 'Nahradit soubor novou verzí'
      : 'Nahrát soubory';
    const subtitle = isReplace && oldFile
      ? `Původní soubor: <span class="font-medium text-gray-700">${this.escape(oldFile.originalName)}</span> bude přesunut do archivu.`
      : 'Soubory budou přiřazeny vybranému zákazníkovi. Můžete přidat více souborů naráz.';

    const customerLocked = !!isReplace;
    const customerLabel = customer?.name || 'Vyberte zákazníka';

    const html = `
      <div id="${modalId}" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
        <div class="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-close="1"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
            <div class="p-6">
              <div class="flex items-start justify-between mb-4">
                <div class="min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900">${title}</h3>
                  <p class="text-sm text-gray-500 mt-1">${subtitle}</p>
                </div>
                <button type="button" data-close="1" class="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1">
                  ${icon('x-mark', 'w-5 h-5')}
                </button>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Zákazník <span class="text-red-500">*</span>
                  </label>
                  <button type="button" id="files-upload-customer-btn"
                    class="w-full inline-flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 ${customerLocked ? 'opacity-60 cursor-not-allowed' : ''}"
                    ${customerLocked ? 'disabled' : ''}>
                    <span id="files-upload-customer-label" class="truncate ${customer ? 'text-gray-900' : 'text-gray-400'}">${this.escape(customerLabel)}</span>
                    ${customerLocked
                      ? icon('lock-closed', 'w-4 h-4 text-gray-400 flex-shrink-0 ml-2')
                      : icon('magnifying-glass', 'w-4 h-4 text-gray-400 flex-shrink-0 ml-2')}
                  </button>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Popis (volitelný)</label>
                  <input type="text" id="files-upload-description"
                    placeholder="Krátký popis souboru, např. „Vizitky – druhá korektura"
                    value="${this.escape(oldFile?.description || '')}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">${isReplace ? 'Soubor' : 'Soubory'}</label>
                  <div id="files-upload-dropzone"
                    class="relative border-2 border-dashed border-gray-300 rounded-lg p-6 transition-colors hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer focus-within:ring-2 focus-within:ring-blue-500">
                    <input type="file" id="files-upload-input"
                      ${isReplace ? '' : 'multiple'}
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer">
                    <div id="files-upload-dropzone-content" class="text-center pointer-events-none">
                      ${icon('cloud-arrow-up', 'mx-auto h-10 w-10 text-gray-400 mb-2')}
                      <p class="text-sm text-gray-700">
                        ${isReplace ? 'Přetáhněte soubor sem nebo' : 'Přetáhněte soubory sem nebo'}
                        <span class="text-blue-600 font-medium">klikněte pro výběr</span>
                      </p>
                      <p id="files-upload-size-hint" class="text-xs text-gray-500 mt-1">Maximální velikost se načítá…</p>
                    </div>
                  </div>
                  <div id="files-upload-file-preview" class="hidden mt-3"></div>
                  <div id="files-upload-progress" class="hidden mt-3">
                    <div class="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span id="files-upload-progress-label">Připravuji…</span>
                      <span id="files-upload-progress-pct" class="tabular-nums">0 %</span>
                    </div>
                    <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div id="files-upload-progress-bar" class="h-full bg-blue-500 transition-all duration-150" style="width: 0%"></div>
                    </div>
                  </div>
                </div>

                <div id="files-upload-error" class="hidden bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800"></div>
              </div>

              <div class="mt-6 flex justify-end gap-2">
                <button type="button" data-close="1"
                  class="inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Zrušit
                </button>
                <button type="button" id="files-upload-submit"
                  class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                  disabled>
                  ${isReplace ? 'Nahradit soubor' : 'Nahrát soubory'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);

    // Interní stav uploadu (uvnitř instance, ne globální)
    this._uploadState = {
      replaceOf,
      isReplace,
      customer: customer ? { ...customer } : null,
      customerLocked,
      files: [],            // pole File objektů (pro replace se použije jen první)
      uploading: false,
      maxFileSizeMb: null,
      storageMode: null     // 'blob' | 'local'
    };

    this.bindUploadModalEvents(modalId);

    // Naplň informaci o limitu z /upload-url (a uložíme storage mode pro odeslání)
    this.fetchUploadConfig().catch((err) => console.warn('Nepodařilo se načíst upload-url:', err));
  }

  bindUploadModalEvents(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.querySelectorAll('[data-close="1"]').forEach((el) => {
      el.addEventListener('click', () => this.closeUploadModal());
    });

    document.getElementById('files-upload-customer-btn')?.addEventListener('click', async () => {
      if (this._uploadState.customerLocked) return;
      if (!window.customersManager) {
        this.notify('Zákazníci nejsou ještě načteni', 'warning');
        return;
      }
      const c = await window.customersManager.openSelector();
      if (!c) return;
      this._uploadState.customer = { id: c.id, name: c.name };
      const lbl = document.getElementById('files-upload-customer-label');
      if (lbl) {
        lbl.textContent = c.name;
        lbl.classList.remove('text-gray-400');
        lbl.classList.add('text-gray-900');
      }
      this.refreshUploadSubmitState();
    });

    const input = document.getElementById('files-upload-input');
    input?.addEventListener('change', (e) => {
      const list = e.target.files;
      if (list && list.length) this.addUploadFiles(list);
      // Reset inputu, aby šel vybrat znovu stejné soubory
      if (input) input.value = '';
    });

    const dropzone = document.getElementById('files-upload-dropzone');
    if (dropzone) {
      ['dragover', 'dragenter'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          dropzone.classList.add('border-blue-500', 'bg-blue-50');
        });
      });
      ['dragleave', 'dragend', 'drop'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          dropzone.classList.remove('border-blue-500', 'bg-blue-50');
        });
      });
      dropzone.addEventListener('drop', (e) => {
        const dt = e.dataTransfer;
        if (dt && dt.files && dt.files.length) {
          this.addUploadFiles(dt.files);
        }
      });
    }

    document.getElementById('files-upload-submit')?.addEventListener('click', () => {
      this.submitUpload();
    });
  }

  async fetchUploadConfig() {
    try {
      const res = await fetch(`${API_BASE_URL}/files/upload-url`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json.data || {};
      this._uploadState.maxFileSizeMb = Number(data.maxFileSizeMb) || 200;
      this._uploadState.storageMode = data.type === 'blob' ? 'blob' : 'local';

      const hint = document.getElementById('files-upload-size-hint');
      if (hint) {
        hint.textContent = `Max. ${this._uploadState.maxFileSizeMb} MB · PDF, obrázky, ZIP a další (kromě spustitelných souborů).`;
      }
    } catch (err) {
      console.warn('Upload config fetch failed:', err);
      // Fallback hodnoty
      this._uploadState.maxFileSizeMb = 200;
      this._uploadState.storageMode = 'local';
      const hint = document.getElementById('files-upload-size-hint');
      if (hint) hint.textContent = 'Max. 200 MB · PDF, obrázky, ZIP a další.';
    }
  }

  addUploadFiles(fileList) {
    this.clearUploadError();
    const maxMb = this._uploadState.maxFileSizeMb || 200;
    const incoming = Array.from(fileList || []);
    const rejected = [];
    const accepted = [];

    for (const file of incoming) {
      const ext = getFileExtension(file.name);
      if (BLOCKED_EXTENSIONS.includes(ext)) {
        rejected.push(`${file.name} (zakázaná přípona .${ext})`);
        continue;
      }
      if (file.size > maxMb * 1024 * 1024) {
        rejected.push(`${file.name} (větší než ${maxMb} MB)`);
        continue;
      }
      accepted.push(file);
    }

    // V replace režimu akceptuj jen jeden soubor (nahraď případný předchozí výběr)
    if (this._uploadState.isReplace && accepted.length > 0) {
      this._uploadState.files = [accepted[0]];
      if (accepted.length > 1) {
        rejected.push(`V režimu nahrazení lze vybrat jen jeden soubor – použit ${accepted[0].name}, zbytek přeskočen.`);
      }
    } else {
      // Deduplikace podle name+size+lastModified, abychom nepřidali stejný soubor 2×
      const existing = this._uploadState.files;
      for (const file of accepted) {
        const dup = existing.some((f) =>
          f.name === file.name && f.size === file.size && f.lastModified === file.lastModified);
        if (!dup) existing.push(file);
      }
    }

    if (rejected.length) {
      this.showUploadError(`Některé soubory byly přeskočeny: ${rejected.join('; ')}.`);
    }

    this.renderUploadPreview();
    this.refreshUploadSubmitState();
  }

  renderUploadPreview() {
    const preview = document.getElementById('files-upload-file-preview');
    if (!preview) return;
    const files = this._uploadState.files || [];

    if (files.length === 0) {
      preview.classList.add('hidden');
      preview.innerHTML = '';
      const dz = document.getElementById('files-upload-dropzone-content');
      dz?.classList.remove('hidden');
      return;
    }

    const totalSize = files.reduce((s, f) => s + (Number(f.size) || 0), 0);
    const items = files.map((file, idx) => {
      const iconSvg = this.getFileIconSvg(file.type, file.name);
      return `
        <div class="flex items-center gap-3 p-2.5 bg-gray-50 border border-gray-200 rounded-md">
          <div class="w-9 h-9 rounded-md bg-white ring-1 ring-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0">
            ${iconSvg}
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate" title="${this.escape(file.name)}">${this.escape(file.name)}</div>
            <div class="text-xs text-gray-500">${this.formatSize(file.size)}${file.type ? ' · ' + this.escape(file.type) : ''}</div>
          </div>
          <button type="button" data-file-remove="${idx}" class="text-gray-400 hover:text-red-500 p-1" title="Odebrat soubor">
            ${icon('x-mark', 'w-4 h-4')}
          </button>
        </div>
      `;
    }).join('');

    preview.innerHTML = `
      <div class="space-y-2">
        ${items}
        <div class="flex items-center justify-between text-xs text-gray-500 px-1">
          <span>${files.length} ${this.plural(files.length, 'soubor', 'soubory', 'souborů')} · ${this.formatSize(totalSize)}</span>
          <button type="button" id="files-upload-files-clear" class="text-gray-500 hover:text-red-500 underline">Odebrat vše</button>
        </div>
      </div>
    `;
    preview.classList.remove('hidden');

    // Skrij dropzone content, když máme soubory (uživatel je může dál přidávat,
    // ale dropzone zůstává funkční – jen skryjeme ikonu)
    const dz = document.getElementById('files-upload-dropzone-content');
    if (dz && !this._uploadState.isReplace) {
      dz.classList.add('hidden');
    }
    preview.querySelectorAll('[data-file-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.fileRemove);
        if (Number.isInteger(idx)) {
          this._uploadState.files.splice(idx, 1);
          this.renderUploadPreview();
          this.refreshUploadSubmitState();
        }
      });
    });

    document.getElementById('files-upload-files-clear')?.addEventListener('click', () => {
      this._uploadState.files = [];
      const input = document.getElementById('files-upload-input');
      if (input) input.value = '';
      this.renderUploadPreview();
      this.refreshUploadSubmitState();
    });
  }

  refreshUploadSubmitState() {
    const btn = document.getElementById('files-upload-submit');
    if (!btn) return;
    const state = this._uploadState;
    const ready = !state.uploading && state.files.length > 0 && !!state.customer?.id;
    btn.disabled = !ready;
  }

  showUploadError(message) {
    const errEl = document.getElementById('files-upload-error');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.classList.remove('hidden');
  }

  clearUploadError() {
    const errEl = document.getElementById('files-upload-error');
    if (!errEl) return;
    errEl.classList.add('hidden');
    errEl.textContent = '';
  }

  setUploadProgress(percent, label) {
    const wrap = document.getElementById('files-upload-progress');
    const bar = document.getElementById('files-upload-progress-bar');
    const pct = document.getElementById('files-upload-progress-pct');
    const lbl = document.getElementById('files-upload-progress-label');
    if (!wrap || !bar || !pct) return;
    wrap.classList.remove('hidden');
    const p = Math.max(0, Math.min(100, Math.round(percent)));
    bar.style.width = `${p}%`;
    pct.textContent = `${p} %`;
    if (label && lbl) lbl.textContent = label;
  }

  hideUploadProgress() {
    document.getElementById('files-upload-progress')?.classList.add('hidden');
  }

  async submitUpload() {
    const state = this._uploadState;
    if (!state || state.uploading || state.files.length === 0 || !state.customer?.id) return;

    const descriptionEl = document.getElementById('files-upload-description');
    const description = descriptionEl ? descriptionEl.value.trim() : '';

    state.uploading = true;
    this.refreshUploadSubmitState();
    document.getElementById('files-upload-submit')?.classList.add('opacity-75');
    this.clearUploadError();
    this.setUploadProgress(0, 'Připravuji…');

    const files = state.isReplace ? state.files.slice(0, 1) : state.files.slice();
    let okCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const baseLabel = files.length > 1
          ? `Soubor ${i + 1}/${files.length}: ${file.name}`
          : file.name;
        try {
          // eslint-disable-next-line no-await-in-loop
          if (state.storageMode === 'blob') {
            await this.uploadViaBlob(state, file, description);
          } else {
            await this.uploadViaLocal(state, file, description);
          }
          okCount += 1;
        } catch (err) {
          console.error(`Chyba při uploadu souboru ${file.name}:`, err);
          errors.push(`${file.name}: ${err.message || 'Upload se nezdařil'}`);
        }
      }

      if (errors.length) {
        if (okCount > 0) {
          this.showUploadError(`Některé soubory se nepodařilo nahrát (${errors.length} z ${files.length}):\n${errors.join('\n')}`);
          this.notify(`${okCount} z ${files.length} souborů nahráno`, 'warning');
        } else {
          this.showUploadError(errors.join('\n'));
        }
        this.hideUploadProgress();
      } else {
        this.setUploadProgress(100, 'Hotovo');
        const msg = state.replaceOf
          ? 'Soubor byl nahrazen'
          : (okCount > 1 ? `${okCount} souborů bylo nahráno` : 'Soubor byl nahrán');
        this.notify(msg, 'success');
        this.closeUploadModal();
      }

      if (okCount > 0) {
        await this.loadFiles();
      }
    } finally {
      state.uploading = false;
      document.getElementById('files-upload-submit')?.classList.remove('opacity-75');
      this.refreshUploadSubmitState();
    }
  }

  /**
   * Dev / fallback flow: XHR multipart POST na /api/files/upload (s progress events).
   */
  uploadViaLocal(state, file, description) {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('customerId', state.customer.id);
      if (state.customer.name) fd.append('customerName', state.customer.name);
      if (description) fd.append('description', description);
      if (state.replaceOf) fd.append('replaceOf', state.replaceOf);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/files/upload`);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = (e.loaded / e.total) * 100;
          // Necháme posledních ~5 % pro server-side zpracování (rename, metadata zápis)
          this.setUploadProgress(Math.min(95, pct), `Nahrávám: ${file.name}`);
        }
      };
      xhr.upload.onloadend = () => {
        this.setUploadProgress(95, `Zpracovávám: ${file.name}`);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText);
            if (json.success) return resolve(json.data);
            return reject(new Error(json.error || 'Upload selhal'));
          } catch (e) {
            return reject(new Error('Neplatná odpověď serveru'));
          }
        }
        let message = `Upload selhal (HTTP ${xhr.status})`;
        try {
          const j = JSON.parse(xhr.responseText);
          if (j && j.error) message = j.error;
        } catch (_) { /* ignore */ }
        reject(new Error(message));
      };
      xhr.onerror = () => reject(new Error('Chyba sítě'));
      xhr.onabort = () => reject(new Error('Upload zrušen'));

      xhr.send(fd);
    });
  }

  /**
   * Production flow: client-upload přímo na Vercel Blob přes @vercel/blob/client.
   * Po úspěchu zapíše metadata přes POST /api/files (s případným replaceOf).
   */
  async uploadViaBlob(state, file, description) {
    // Dynamický import – parcel vytvoří separátní chunk a stáhne ho jen v blob režimu
    const { upload } = await import('@vercel/blob/client');

    const clientPayload = JSON.stringify({
      customerId: state.customer.id,
      description: description || '',
      replaceOf: state.replaceOf || null
    });

    const result = await upload(file.name, file, {
      access: 'public',
      handleUploadUrl: `${API_BASE_URL}/files/upload-token`,
      clientPayload,
      onUploadProgress: ({ percentage }) => {
        this.setUploadProgress(Math.min(95, percentage), `Nahrávám: ${file.name}`);
      }
    });

    this.setUploadProgress(96, `Ukládám metadata: ${file.name}`);

    // Zapiš metadata na backend
    const metaRes = await fetch(`${API_BASE_URL}/files`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        originalName: file.name,
        size: file.size,
        mimeType: file.type,
        storageUrl: result.url,
        customerId: state.customer.id,
        customerName: state.customer.name || '',
        description: description || '',
        replaceOf: state.replaceOf || null
      })
    });
    if (!metaRes.ok) {
      const text = await metaRes.text().catch(() => '');
      throw new Error(text || `Uložení metadat selhalo (HTTP ${metaRes.status})`);
    }
    const json = await metaRes.json();
    if (!json.success) throw new Error(json.error || 'Uložení metadat selhalo');
    return json.data;
  }

  closeUploadModal() {
    this.removeModal('files-upload-modal');
    this._uploadState = null;
  }

  removeModal(id) {
    const el = document.getElementById(id);
    if (el && el.parentNode) {
      el.parentNode.remove();
    }
  }

  // ===== Stats panel =====

  renderStats() {
    const el = document.getElementById('files-stats');
    if (!el) return;
    if (!this.stats) {
      el.innerHTML = '';
      return;
    }

    const t = this.stats.totals || {};
    const perCustomer = this.stats.perCustomer || [];

    // Pokud je vybrán filtr, najdi statistiky daného zákazníka
    const selectedStat = this.selectedCustomer
      ? perCustomer.find((p) => p.customerId === this.selectedCustomer.id)
      : null;

    const topThree = perCustomer.slice(0, 3);

    el.innerHTML = `
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        ${this.renderStatCard({
          label: 'Celkové místo',
          value: this.formatSize(t.size || 0),
          sub: `${(t.count || 0).toLocaleString('cs-CZ')} ${this.plural(t.count || 0, 'soubor', 'soubory', 'souborů')}`,
          color: 'blue',
          icon: 'folder'
        })}
        ${this.renderStatCard({
          label: 'Aktivní',
          value: this.formatSize(t.activeSize || 0),
          sub: `${(t.activeCount || 0).toLocaleString('cs-CZ')} ${this.plural(t.activeCount || 0, 'soubor', 'soubory', 'souborů')}`,
          color: 'emerald',
          icon: 'check'
        })}
        ${this.renderStatCard({
          label: 'Archivováno',
          value: this.formatSize(t.archivedSize || 0),
          sub: `${(t.archivedCount || 0).toLocaleString('cs-CZ')} ${this.plural(t.archivedCount || 0, 'soubor', 'soubory', 'souborů')}`,
          color: 'gray',
          icon: 'archive-box'
        })}
        ${this.renderStatCard({
          label: selectedStat ? 'Vybraný zákazník' : 'Zákazníci se soubory',
          value: selectedStat ? this.formatSize(selectedStat.size || 0) : (perCustomer.length).toLocaleString('cs-CZ'),
          sub: selectedStat
            ? `${(selectedStat.count || 0).toLocaleString('cs-CZ')} ${this.plural(selectedStat.count || 0, 'soubor', 'soubory', 'souborů')}`
            : (perCustomer.length === 1 ? '1 zákazník' : `${perCustomer.length} zákazníků`),
          color: 'indigo',
          icon: 'user-group'
        })}
      </div>

      ${(!this.selectedCustomer && topThree.length > 0) ? `
        <div class="mt-3 bg-white rounded-lg border border-gray-200 p-3">
          <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Největší zákazníci podle velikosti souborů</div>
          <div class="flex flex-wrap gap-2">
            ${topThree.map((c) => `
              <button type="button" data-customer-id="${this.escape(c.customerId || '')}" data-customer-name="${this.escape(c.customerName || '')}"
                class="files-stats-customer inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs bg-gray-50 hover:bg-blue-50 hover:text-blue-700 ring-1 ring-gray-200 hover:ring-blue-200 transition-colors">
                <span class="font-medium truncate max-w-[12rem]">${this.escape(c.customerName || '(bez jména)')}</span>
                <span class="text-gray-500">${this.formatSize(c.size || 0)}</span>
                <span class="text-gray-400">·</span>
                <span class="text-gray-500">${c.count}×</span>
              </button>`).join('')}
          </div>
        </div>` : ''}
    `;

    // Klik na kartičku zákazníka v Top 3 → nastavit filtr
    el.querySelectorAll('.files-stats-customer').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.customerId;
        const name = btn.dataset.customerName;
        if (!id) return;
        this.selectedCustomer = { id, name };
        this.updateCustomerLabel();
        this.loadFiles();
      });
    });
  }

  renderStatCard({ label, value, sub, color, icon: iconName }) {
    const colors = {
      blue:    { bg: 'bg-blue-50',    text: 'text-blue-600',    accent: 'text-blue-900' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', accent: 'text-emerald-900' },
      gray:    { bg: 'bg-gray-50',    text: 'text-gray-500',    accent: 'text-gray-900' },
      indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  accent: 'text-indigo-900' }
    };
    const c = colors[color] || colors.gray;
    return `
      <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-start gap-3 shadow-sm">
        <div class="flex-shrink-0 w-9 h-9 rounded-md ${c.bg} ${c.text} flex items-center justify-center">
          ${icon(iconName, 'w-5 h-5')}
        </div>
        <div class="min-w-0">
          <div class="text-[11px] uppercase tracking-wide text-gray-500 font-medium">${this.escape(label)}</div>
          <div class="text-base sm:text-lg font-semibold ${c.accent} truncate tabular-nums" title="${this.escape(String(value))}">${this.escape(String(value))}</div>
          <div class="text-xs text-gray-500">${this.escape(sub || '')}</div>
        </div>
      </div>
    `;
  }

  // ===== Maintenance modal =====

  openMaintenanceModal() {
    const modalId = 'files-maintenance-modal';
    this.removeModal(modalId);

    const totals = this.stats?.totals || {};
    const totalSizeStr = this.formatSize(totals.size || 0);
    const totalCount = totals.count || 0;

    // Předvyplnit filtr aktuálně vybraného zákazníka, pokud existuje
    const scopedCustomer = this.selectedCustomer ? { ...this.selectedCustomer } : null;

    const html = `
      <div id="${modalId}" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
        <div class="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-close="1"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="p-6">
              <div class="flex items-start justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Údržba úložiště</h3>
                  <p class="text-sm text-gray-500 mt-1">Hromadně smaže staré soubory podle vybraných kritérií.</p>
                </div>
                <button type="button" data-close="1" class="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1">
                  ${icon('x-mark', 'w-5 h-5')}
                </button>
              </div>

              <div class="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 text-xs text-gray-600">
                Aktuálně uložené: <strong class="text-gray-900">${this.escape(totalSizeStr)}</strong> v ${totalCount.toLocaleString('cs-CZ')} ${this.plural(totalCount, 'souboru', 'souborech', 'souborech')}.
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Smazat soubory starší než</label>
                  <div class="flex items-center gap-2">
                    <input type="number" id="files-maint-days" min="1" step="1" value="90"
                      class="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums">
                    <span class="text-sm text-gray-600">dní</span>
                  </div>
                  <div class="mt-2 flex flex-wrap gap-1.5">
                    ${[30, 90, 180, 365].map((d) => `
                      <button type="button" data-preset="${d}"
                        class="files-maint-preset inline-flex items-center px-2 py-0.5 rounded-md text-xs border border-gray-200 text-gray-600 hover:bg-gray-50">
                        ${d} dní
                      </button>`).join('')}
                  </div>
                </div>

                <div>
                  <label class="flex items-center gap-2 mb-1">
                    <input type="checkbox" id="files-maint-scope-customer" ${scopedCustomer ? 'checked' : ''}
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                    <span class="text-sm font-medium text-gray-700">Omezit na jednoho zákazníka</span>
                  </label>
                  <div id="files-maint-customer-row" class="${scopedCustomer ? '' : 'hidden'} pl-6">
                    <button type="button" id="files-maint-customer-btn"
                      class="w-full inline-flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50">
                      <span id="files-maint-customer-label" class="truncate ${scopedCustomer ? 'text-gray-900' : 'text-gray-400'}">
                        ${this.escape(scopedCustomer?.name || 'Vybrat zákazníka')}
                      </span>
                      ${icon('magnifying-glass', 'w-4 h-4 text-gray-400 flex-shrink-0 ml-2')}
                    </button>
                  </div>
                </div>

                <label class="flex items-center gap-2">
                  <input type="checkbox" id="files-maint-include-archived" checked
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                  <span class="text-sm text-gray-700">Smazat i archivované verze</span>
                </label>
              </div>

              <div id="files-maint-preview" class="mt-4 hidden">
                <div class="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                  <div class="font-semibold mb-1">K odstranění připraveno:</div>
                  <div id="files-maint-preview-text" class="text-amber-800"></div>
                </div>
              </div>

              <div id="files-maint-error" class="hidden mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800"></div>

              <div class="mt-6 flex justify-end gap-2 flex-wrap">
                <button type="button" data-close="1"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Zrušit
                </button>
                <button type="button" id="files-maint-preview-btn"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Náhled (počet souborů)
                </button>
                <button type="button" id="files-maint-purge-btn"
                  class="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed">
                  ${icon('trash', 'w-4 h-4 mr-2')}
                  Smazat
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    document.body.appendChild(wrap);

    this._maintState = { customer: scopedCustomer };

    this.bindMaintenanceModalEvents(modalId);
  }

  bindMaintenanceModalEvents(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    const close = () => this.removeModal(modalId);
    modal.querySelectorAll('[data-close="1"]').forEach((el) => el.addEventListener('click', close));

    // Presety dní
    modal.querySelectorAll('.files-maint-preset').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = document.getElementById('files-maint-days');
        if (input) input.value = btn.dataset.preset;
        document.getElementById('files-maint-preview')?.classList.add('hidden');
      });
    });

    // Zobrazení / skrytí řádku s customerem
    const scopeChk = document.getElementById('files-maint-scope-customer');
    const customerRow = document.getElementById('files-maint-customer-row');
    scopeChk?.addEventListener('change', () => {
      if (scopeChk.checked) {
        customerRow?.classList.remove('hidden');
      } else {
        customerRow?.classList.add('hidden');
        this._maintState.customer = null;
        this.updateMaintenanceCustomerLabel();
      }
      document.getElementById('files-maint-preview')?.classList.add('hidden');
    });

    document.getElementById('files-maint-customer-btn')?.addEventListener('click', async () => {
      if (!window.customersManager) {
        this.notify('Zákazníci nejsou ještě načteni', 'warning');
        return;
      }
      const c = await window.customersManager.openSelector();
      if (!c) return;
      this._maintState.customer = { id: c.id, name: c.name };
      this.updateMaintenanceCustomerLabel();
      document.getElementById('files-maint-preview')?.classList.add('hidden');
    });

    document.getElementById('files-maint-preview-btn')?.addEventListener('click', () => this.runMaintenance({ dryRun: true }));
    document.getElementById('files-maint-purge-btn')?.addEventListener('click', () => this.runMaintenance({ dryRun: false }));
  }

  updateMaintenanceCustomerLabel() {
    const lbl = document.getElementById('files-maint-customer-label');
    if (!lbl) return;
    const c = this._maintState?.customer;
    if (c) {
      lbl.textContent = c.name;
      lbl.classList.remove('text-gray-400');
      lbl.classList.add('text-gray-900');
    } else {
      lbl.textContent = 'Vybrat zákazníka';
      lbl.classList.add('text-gray-400');
      lbl.classList.remove('text-gray-900');
    }
  }

  async runMaintenance({ dryRun }) {
    const errEl = document.getElementById('files-maint-error');
    const previewEl = document.getElementById('files-maint-preview');
    const previewText = document.getElementById('files-maint-preview-text');
    errEl?.classList.add('hidden');

    const days = Number(document.getElementById('files-maint-days')?.value);
    if (!Number.isFinite(days) || days <= 0) {
      if (errEl) {
        errEl.textContent = 'Zadejte kladný počet dní.';
        errEl.classList.remove('hidden');
      }
      return;
    }
    const scopeChecked = document.getElementById('files-maint-scope-customer')?.checked;
    const customer = scopeChecked ? this._maintState?.customer : null;
    if (scopeChecked && !customer) {
      if (errEl) {
        errEl.textContent = 'Vyberte zákazníka, na kterého se má údržba omezit.';
        errEl.classList.remove('hidden');
      }
      return;
    }
    const includeArchived = !!document.getElementById('files-maint-include-archived')?.checked;

    // Pro skutečné mazání ještě confirm
    if (!dryRun) {
      const previewVisible = previewEl && !previewEl.classList.contains('hidden');
      const scopeText = customer ? ` zákazníka „${customer.name}"` : '';
      const message = previewVisible
        ? `Opravdu nevratně smazat soubory${scopeText} starší než ${days} dní?`
        : `Opravdu nevratně smazat všechny soubory${scopeText} starší než ${days} dní?\n\nDoporučujeme nejdřív zobrazit náhled.`;
      if (!confirm(message)) return;
    }

    const purgeBtn = document.getElementById('files-maint-purge-btn');
    const previewBtn = document.getElementById('files-maint-preview-btn');
    if (purgeBtn) purgeBtn.disabled = true;
    if (previewBtn) previewBtn.disabled = true;

    try {
      const res = await fetch(`${API_BASE_URL}/files/maintenance/purge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days,
          customerId: customer?.id || null,
          includeArchived,
          dryRun
        })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const data = json.data || {};

      if (dryRun) {
        const count = data.matchedCount || 0;
        const size = this.formatSize(data.matchedSize || 0);
        if (previewText) {
          previewText.innerHTML = count === 0
            ? `Žádný soubor neodpovídá kritériím – není co mazat.`
            : `<strong>${count}</strong> ${this.plural(count, 'soubor', 'soubory', 'souborů')} · ${this.escape(size)}`;
        }
        previewEl?.classList.remove('hidden');
      } else {
        const count = data.deletedCount || 0;
        const size = this.formatSize(data.deletedSize || 0);
        if (count === 0) {
          this.notify('Žádný soubor nesplňoval kritéria – nic nebylo smazáno.', 'info');
        } else {
          let msg = `Smazáno ${count} ${this.plural(count, 'soubor', 'soubory', 'souborů')} (${size}).`;
          if (data.binaryErrors > 0) msg += ` Pozn.: ${data.binaryErrors} binárních souborů se nepodařilo smazat z disku.`;
          this.notify(msg, count > 0 ? 'success' : 'info');
        }
        this.removeModal('files-maintenance-modal');
        this.loadFiles();
      }
    } catch (err) {
      console.error('Údržba selhala:', err);
      if (errEl) {
        errEl.textContent = err.message || 'Údržba se nezdařila';
        errEl.classList.remove('hidden');
      }
    } finally {
      if (purgeBtn) purgeBtn.disabled = false;
      if (previewBtn) previewBtn.disabled = false;
    }
  }

  // ===== Utility =====

  formatSize(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  formatDateTime(iso) {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('cs-CZ', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (_) {
      return '-';
    }
  }

  plural(n, one, few, many) {
    const abs = Math.abs(n);
    if (abs === 1) return one;
    if (abs >= 2 && abs <= 4) return few;
    return many;
  }

  /**
   * Vrací inline SVG ikonu podle typu souboru (sdílená implementace v icons.js).
   */
  getFileIconSvg(mimeType, filename) {
    return getFileIconSvg(mimeType, filename);
  }

  async copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    // Fallback (insecure context / staré prohlížeče)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } finally {
      ta.remove();
    }
  }

  notify(msg, type = 'info') {
    if (typeof window.showNotification === 'function') {
      window.showNotification(msg, type);
    } else {
      console.log(`[${type}] ${msg}`);
    }
  }

  escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

window.FilesManager = FilesManager;
